import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { DEFAULT_DECIMAL, FORMATTERS, formatPercentSign, formatPP } from "app/common/benchmark/formatters";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import { selectAssetsImages } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import type {
    AbsolutePrimarySchema,
    RankingDataSchema,
    RelativePrimarySchema,
} from "app/pages/benchmark-tracker/types/api.types";

import type { MetricFormat } from "app/common/benchmark/types";
import Change from "../Change/Change";
import { LOADING_PLACEHOLDER_HEADER_HEIGHT, LOADING_PLACEHOLDER_WIDTH } from "../constants";
import { getAxisLineData } from "../helpers";
import RankingAxis from "../RankingAxis/RankingAxis";
import type { Axis } from "app/components/RankingAxis/types";

import { Divider, HeaderContainer, LoaderWrapper } from "./styles";

type RankingHeaderSchema = {
    format: MetricFormat;
    data: RankingDataSchema;
    tooltip: string;
    title: string;
    isInverted: boolean;
    isLoading: boolean;
    isRelative?: boolean;
};

const RankingHeader = ({ data, format, tooltip, title, isInverted, isLoading, isRelative }: RankingHeaderSchema) => {
    const {
        params: { granularity },
    } = useCurrentStateAndParams();

    const selectedBenchmark = useSelector(selectSelectedBenchmark);
    const domainImages = useSelector(selectAssetsImages);

    const formatter = FORMATTERS[format];

    const [axisLineData, setAxisLineData] = useState<Axis[]>();

    useEffect(() => {
        if (!data || !selectedBenchmark) {
            return;
        }

        const competitors = selectedBenchmark?.activationSet?.competitorsDomains || [];
        const domains = [selectedBenchmark?.activationSet?.primaryDomain, ...competitors].filter(
            (s): s is string => s !== undefined,
        );

        const axisData = getAxisLineData(data, selectedBenchmark, domainImages, domains);

        setAxisLineData(axisData);
    }, [data, domainImages, selectedBenchmark]);

    const periodOverPeriodData = isRelative
        ? (data?.primaryData as RelativePrimarySchema)?.periodOverPeriodChangeDifference
        : (data?.primaryData as AbsolutePrimarySchema)?.periodOverPeriodChangeRate;
    const convertedToPlus = periodOverPeriodData < 0 ? -periodOverPeriodData : periodOverPeriodData;
    const change = isRelative ? formatPP(convertedToPlus) : formatPercentSign(convertedToPlus, DEFAULT_DECIMAL);

    return (
        <HeaderContainer>
            <Change
                tooltip={tooltip}
                title={title}
                isIncreasing={periodOverPeriodData > 0}
                isNotChanged={periodOverPeriodData === 0}
                isInverted={isInverted}
                change={change}
                value={formatter(data?.primaryData?.absoluteValue ?? 0)}
                granularity={granularity}
                isLoading={isLoading}
            />
            <Divider />
            {isLoading ? (
                <LoaderWrapper>
                    <PixelPlaceholderLoader
                        width={LOADING_PLACEHOLDER_WIDTH}
                        height={LOADING_PLACEHOLDER_HEADER_HEIGHT}
                    />
                </LoaderWrapper>
            ) : (
                <RankingAxis
                    data={axisLineData ?? []}
                    format={format}
                />
            )}
        </HeaderContainer>
    );
};

export default RankingHeader;
